import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import AuthDialog from "../components/AuthDialog";
import { ACADEMY_LOGO } from "../data/assets";
import { CheckCircle2, Loader2, XCircle, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { player } = usePlayerAuth();
  const [state, setState] = useState("checking"); // checking | paid | error
  const [email, setEmail] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  const poll = useCallback(async (attempt = 0) => {
    if (attempt > 16) { setState("error"); return; }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setState("paid");
        setEmail(data.buyer_email || "");
        return;
      }
    } catch (e) { /* transient error — retry */ }
    setTimeout(() => poll(attempt + 1), Math.min(1500 + attempt * 300, 3000));
  }, [sessionId]);

  useEffect(() => { if (sessionId) poll(); else setState("error"); }, [sessionId, poll]);

  return (
    <div className="min-h-screen bg-[#111] text-white flex items-center justify-center px-6 grain">
      <div className="absolute inset-0 red-glow pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative z-10 max-w-md w-full bg-[#141414] border border-white/10 rounded-xl p-8 text-center">
        <img src={ACADEMY_LOGO} alt="" className="w-44 h-auto object-contain mx-auto mb-5" />
        {state === "checking" && (<><Loader2 size={40} className="text-[#C41E24] mx-auto animate-spin mb-4" /><h1 className="font-headings text-2xl uppercase">Confirming your purchase…</h1></>)}
        {state === "error" && (<><XCircle size={40} className="text-[#C41E24] mx-auto mb-4" /><h1 className="font-headings text-2xl uppercase mb-2">Hmm, we couldn't confirm yet</h1><p className="text-white/60 text-sm mb-5">If you were charged, check your email for setup instructions, or contact your coach.</p><button onClick={() => navigate("/")} className="bg-[#C41E24] px-6 py-3 rounded-sm font-bold uppercase text-sm">Back Home</button></>)}
        {state === "paid" && (
          <>
            <CheckCircle2 size={44} className="text-green-500 mx-auto mb-4" />
            <h1 className="font-headings text-3xl uppercase leading-none mb-2">You're In!</h1>
            <p className="text-white/65 text-sm mb-6">The Next O Technical Academy is unlocked — you're enrolled in Volume 1: Ball Mastery Blueprint. We've emailed a <b className="text-white">secure setup link</b> to <b className="text-white">{email || "your email"}</b> — click it to create your password and open your Hub.</p>
            {player ? (
              <button data-testid="success-hub-btn" onClick={() => navigate("/hub")} className="w-full bg-[#C41E24] hover:bg-[#A0161D] py-3 rounded-sm font-subheadings font-bold uppercase tracking-wider">Go to My Hub</button>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 text-white/50 text-sm mb-4"><Mail size={16} className="text-[#C41E24]" /> Check your inbox for the setup link</div>
                <button data-testid="success-login-btn" onClick={() => setShowAuth(true)} className="w-full bg-transparent border border-white/25 hover:border-white py-3 rounded-sm font-subheadings font-bold uppercase tracking-wider">Already set up? Log In</button>
              </>
            )}
          </>
        )}
      </motion.div>
      <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} onSuccess={() => navigate("/hub")} />
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#111] text-white flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-[#141414] border border-white/10 rounded-xl p-8 text-center">
        <img src={ACADEMY_LOGO} alt="" className="w-40 h-auto object-contain mx-auto mb-4" />
        <h1 className="font-headings text-2xl uppercase mb-2">Checkout Cancelled</h1>
        <p className="text-white/60 text-sm mb-5">No worries — your Blueprint is still waiting whenever you're ready to enroll.</p>
        <button data-testid="cancel-home-btn" onClick={() => navigate("/")} className="bg-[#C41E24] px-6 py-3 rounded-sm font-bold uppercase text-sm">Back Home</button>
      </div>
    </div>
  );
}
